/* Dated evidence only. Interest and ticket demand are never a live headcount. */
(() => {
  'use strict';
  const MINUTE=60000,HOUR=60*MINUTE,DAY=24*HOUR;
  const REPORT_TTL=HOUR,DEMAND_TTL=DAY;
  const own=(value,key)=>Object.prototype.hasOwnProperty.call(value,key);
  const statusOrder={busy:0,quiet:1,closed:2};
  const labels={busy:'Reported busy · just checked',demand:'Ticket demand · check arrival','allocation-sold-out':'Ticket allocation sold out · check entry','ticket-sales':'Tickets sold · crowd unconfirmed','event-listed':'Event listed · crowd unconfirmed',unconfirmed:'Crowd unconfirmed',ended:'Listed night ended',quiet:'Quiet · choose another room',closed:'Reported closed',past:'Past night'};
  const ranks={busy:100,demand:80,'allocation-sold-out':60,'ticket-sales':55,'event-listed':50,unconfirmed:30,ended:10,quiet:5,closed:0,past:0};

  function validNight(value){
    if(typeof value!=='string'||!/^\d{4}-\d{2}-\d{2}$/.test(value))return false;
    const [year,month,day]=value.split('-').map(Number);
    const leap=year%4===0&&(year%100!==0||year%400===0);
    return year>=1&&month>=1&&month<=12&&day>=1&&day<=[31,leap?29:28,31,30,31,30,31,31,30,31,30,31][month-1];
  }
  function instant(value){
    if(typeof value!=='string')return null;
    const match=/^(\d{4}-\d{2}-\d{2})T([01]\d|2[0-3]):([0-5]\d):([0-5]\d)(?:\.\d{1,3})?(Z|[+-](?:0\d|1[0-4]):[0-5]\d)$/.exec(value);
    if(!match||!validNight(match[1])||(/[+-]14:/.test(match[5])&&!match[5].endsWith(':00')))return null;
    const result=Date.parse(value);
    return Number.isFinite(result)?result:null;
  }
  const iso=ms=>new Date(ms).toISOString();
  function nightAt(now){
    const ms=instant(now);
    if(ms===null)return null;
    // Shanghai is UTC+8; shift back six hours to keep 00:00–05:59 with the previous party night.
    const night=iso(ms+2*HOUR).slice(0,10);
    return validNight(night)?night:null;
  }
  function httpsSource(value){
    if(typeof value!=='string'||value!==value.trim())return false;
    try{const url=new URL(value);return url.protocol==='https:'&&!!url.hostname&&!url.username&&!url.password;}catch{return false;}
  }
  function validCounters(item){
    if(own(item,'count')&&(!Number.isSafeInteger(item.count)||item.count<0))return false;
    if(own(item,'capacity')&&(!Number.isSafeInteger(item.capacity)||item.capacity<=0))return false;
    return !(own(item,'count')&&own(item,'capacity')&&item.count>item.capacity);
  }
  function bounds(event){
    const time=/^(?:[01]\d|2[0-3]):[0-5]\d$/;
    if(!time.test(event.start||'')||!time.test(event.end||''))return null;
    const start=instant(`${event.date}T${event.start}:00+08:00`);
    let end=instant(`${event.date}T${event.end}:00+08:00`);
    if(start===null||end===null)return null;
    if(end<=start)end+=DAY;
    return {start,end};
  }
  function signalFor(item,nowMs,ttl,text){
    const observedMs=item._at;
    const signal={kind:item.kind,sourceLabel:item.sourceLabel||(item.kind==='device-report'?'Your check-in':'Dated source'),observedAt:iso(observedMs),expiresAt:iso(observedMs+ttl),text};
    for(const key of ['sourceUrl','status','count','capacity','inventoryScope'])if(own(item,key))signal[key]=item[key];
    return {signal,freshness:{observedAt:signal.observedAt,expiresAt:signal.expiresAt,ageMinutes:(nowMs-observedMs)/MINUTE,ttlMinutes:ttl/MINUTE}};
  }
  function evaluate(input={}){
    const {venue,events=[],night,now,evidence=[],report=null}=input||{};
    const venueId=typeof venue==='string'?venue:venue?.id;
    const nowMs=instant(now),currentNight=nightAt(now);
    const output=(state,detail,eventList=[],extra={})=>({state,label:labels[state],detail,rank:ranks[state],events:eventList,...extra});
    if(typeof venueId!=='string'||!venueId||!validNight(night)||nowMs===null||!currentNight)return output('unconfirmed','A valid venue, Shanghai night and current timestamp are needed to check evidence.');

    const matching=(Array.isArray(events)?events:[]).filter(event=>event&&!event.postponed&&!['postponed','cancelled'].includes(event.status)&&event.venue===venueId&&validNight(event.date)&&event.date===night);
    if(night<currentNight)return output('past','This is an earlier Shanghai party night. Old reports do not describe the room now.',matching);
    const available=matching.filter(event=>{const times=bounds(event);return !times||nowMs<times.end;});
    const current=night===currentNight;
    const accepted=(Array.isArray(evidence)?evidence:[]).flatMap(item=>{
      if(!item||item.venueId!==venueId||!validNight(item.night)||item.night!==night||!httpsSource(item.sourceUrl)||!validCounters(item))return [];
      if(own(item,'eventId')&&(typeof item.eventId!=='string'||!available.some(event=>event.id===item.eventId)))return [];
      const at=instant(item.observedAt);
      if(at===null||at>nowMs)return [];
      if(!['ticket-sales','sold-out','ra-interest','venue-confirmation'].includes(item.kind))return [];
      if(['ticket-sales','ra-interest'].includes(item.kind)&&!own(item,'count'))return [];
      if(item.kind==='sold-out'&&((own(item,'count')&&item.count===0)||(own(item,'count')&&own(item,'capacity')&&item.count!==item.capacity)))return [];
      if(item.kind==='venue-confirmation'&&(!current||!own(statusOrder,item.status)||nowMs-at>REPORT_TTL))return [];
      if(item.kind!=='venue-confirmation'&&nowMs-at>DEMAND_TTL)return [];
      return [{...item,_at:at}];
    });
    const direct=accepted.filter(item=>item.kind==='venue-confirmation');
    const reportedAt=instant(report?.reportedAt);
    if(current&&report&&report.night===night&&own(statusOrder,report.status)&&reportedAt!==null&&reportedAt<=nowMs&&nowMs-reportedAt<=REPORT_TTL){
      direct.push({kind:'device-report',status:report.status,sourceLabel:'Your check-in',_at:reportedAt});
    }
    direct.sort((a,b)=>b._at-a._at||statusOrder[b.status]-statusOrder[a.status]||(a.kind==='device-report'?-1:b.kind==='device-report'?1:0));
    const observed=direct[0];
    if(observed&&observed.status!=='busy'){
      const detail=observed.status==='closed'?'A fresh check reports the venue closed. Choose another place.':'A fresh check reports a quiet room. Choose another room if you want a crowd.';
      return output(observed.status,detail,available,signalFor(observed,nowMs,REPORT_TTL,detail));
    }
    // A recent pre-closing crowd report is not a recommendation to arrive after the advertised end.
    if(matching.length&&!available.length)return output('ended','Every listed event for this venue and night has reached its published end time. Check a later opening before travelling.',matching);
    if(observed){
      const detail=observed.kind==='device-report'?'You reported the room busy within the last hour. This check-in stays on this device and expires after 60 minutes.':'A dated venue confirmation reports the room busy within the last hour. It expires after 60 minutes.';
      return output('busy',detail,available,{...signalFor(observed,nowMs,REPORT_TTL,detail),label:observed.kind==='device-report'?'You confirmed busy':labels.busy});
    }

    // Use the latest observation of each inventory so an older sold-out snapshot cannot beat a newer correction.
    const latest=new Map();
    accepted.filter(item=>item.kind==='ticket-sales'||item.kind==='sold-out').forEach(item=>{
      const channel=item.inventoryScope==='whole-event'?'whole-event':new URL(item.sourceUrl).hostname;
      const key=`${item.eventId||''}|${channel}|${item.inventoryScope||'unknown'}`;
      const previous=latest.get(key);
      if(!previous||item._at>previous._at||(item._at===previous._at&&item.kind==='ticket-sales'))latest.set(key,item);
    });
    const ticketSignals=[...latest.values()].map(item=>{
      if(item.kind==='sold-out'){
        const whole=item.inventoryScope==='whole-event';
        const ra=/(^|\.)ra\.co$/.test(new URL(item.sourceUrl).hostname);
        const label=whole?'Sold out · check entry':ra?'RA allocation sold out · check entry':labels['allocation-sold-out'];
        const detail=whole?'The source reports the whole event sold out. Confirm entry before travelling; ticket demand does not confirm a busy room now.':'The source reports this ticket allocation sold out. Other allocations, WeChat sales and door entry may differ; confirm entry before travelling.';
        return {item,state:whole?'demand':'allocation-sold-out',label,detail};
      }
      const whole=item.inventoryScope==='whole-event';
      const strong=whole&&own(item,'capacity')&&item.count/item.capacity>=0.7;
      const scope=whole?'whole-event inventory':'reported ticket inventory';
      const detail=`The source reports ${item.count}${own(item,'capacity')?' of '+item.capacity:''} tickets sold in the ${scope}. ${strong?'Confirm arrival and entry; ticket demand is not a live headcount.':'The on-site crowd is unconfirmed; other sales channels and door arrivals may differ.'}`;
      return {item,state:strong?'demand':item.count>0?'ticket-sales':null,detail};
    }).sort((a,b)=>(ranks[b.state]||0)-(ranks[a.state]||0)||b.item._at-a.item._at);
    const ticket=ticketSignals.find(candidate=>candidate.state);
    if(ticket)return output(ticket.state,ticket.detail,available,{...signalFor(ticket.item,nowMs,DEMAND_TTL,ticket.detail),...(ticket.label?{label:ticket.label}:{})});

    const base=available.length?'event-listed':'unconfirmed';
    const baseDetail=available.length?(available.some(event=>!bounds(event))?'A dated event is listed, with some hours still unconfirmed. Check the current listing; a listing is not a crowd report.':'A dated event is listed and has not reached its published end. Check entry and current activity; a listing is not a crowd report.'):'No fresh crowd confirmation or matching event listing is available for this venue and night. Unknown does not mean empty.';
    const interest=accepted.filter(item=>item.kind==='ra-interest').sort((a,b)=>b._at-a._at)[0];
    if(interest){
      const detail=`${baseDetail} ${interest.count} interested, not sales. Other ticket channels and door arrivals are not counted.`;
      return output(base,detail,available,signalFor(interest,nowMs,DEMAND_TTL,`${interest.count} interested, not sales. This does not measure the room or WeChat and door sales.`));
    }
    const zero=ticketSignals.find(candidate=>!candidate.state);
    if(zero)return output(base,`${baseDetail} ${zero.detail}`,available,signalFor(zero.item,nowMs,DEMAND_TTL,zero.detail));
    return output(base,baseDetail,available);
  }
  globalThis.SHANGHAI_CROWD=Object.freeze({evaluate,nightAt});
})();
